import { useApp, useTick } from "@pixi/react"
import type { DisplayObject } from "pixi.js"
import { useCallback, useEffect, useRef } from "react"
import { NODE_ANIMATION_DURATION } from "@/domains/visualization/constants/graph"
import type { PositionedEdge } from "@/domains/visualization/types/Graph"
import {
  type AnimationMap,
  type AnimationState,
  detectEdgeChanges,
  initializeChangedEdgeAnimation,
  initializeNewEdgeAnimation,
  updateEdgePosition
} from "./useEdgeAnimation.utils"

/**
 * A hook that manages smooth animations for edges in a graph visualization.
 * It handles both path updates for existing edges and animations for new edges.
 *
 * @param positionedEdges - Current state of all edges with positions
 * @param layoutReady - Whether the graph layout is ready for animation
 */
export const useEdgeAnimation = (
  positionedEdges: PositionedEdge[],
  currentNodeInstance: DisplayObject | null,
  layoutReady: boolean
) => {
  const { stage } = useApp()

  // Ref to store the previous state of edges for comparison
  const previousEdgesRef = useRef<PositionedEdge[]>([])

  // Ref to store active animations
  const activeAnimationsRef = useRef<AnimationMap>(new Map())

  useEffect(() => {
    if (!layoutReady) {
      previousEdgesRef.current = positionedEdges
      return
    }

    const { changedEdges, newEdges } = detectEdgeChanges(positionedEdges, previousEdgesRef.current)
    const newAnimations = new Map<string, AnimationState>()

    // Handle changed edges
    for (const changedEdge of changedEdges) {
      const previousEdge = previousEdgesRef.current.find((e) => e.id === changedEdge.id)
      if (!previousEdge) continue

      const animation = initializeChangedEdgeAnimation(changedEdge, previousEdge, stage)
      if (animation) {
        newAnimations.set(changedEdge.id, animation)
      }
    }

    // Handle new edges
    if (currentNodeInstance && newEdges.length > 0) {
      const sourcePosition = {
        x: currentNodeInstance.x,
        y: currentNodeInstance.y + currentNodeInstance.getBounds().height
      }

      for (const newEdge of newEdges) {
        const animation = initializeNewEdgeAnimation(newEdge, sourcePosition, stage)
        if (animation) {
          newAnimations.set(newEdge.id, animation)
        }
      }
    }

    if (newAnimations.size > 0) {
      activeAnimationsRef.current = newAnimations
    }

    previousEdgesRef.current = positionedEdges
  }, [positionedEdges, layoutReady, stage, currentNodeInstance])

  useTick(
    useCallback(() => {
      if (activeAnimationsRef.current.size === 0) {
        return
      }

      const currentTime = performance.now()
      const completedAnimations: string[] = []

      // Update each active animation and check for invalid instances
      for (const [edgeId, animation] of activeAnimationsRef.current) {
        // Check if the edge instance is still valid before updating
        if (!animation.edgeInstance || animation.edgeInstance.destroyed) {
          completedAnimations.push(edgeId)
          continue
        }

        const completed = updateEdgePosition(animation, currentTime, NODE_ANIMATION_DURATION)

        if (completed) {
          completedAnimations.push(edgeId)
        }
      }

      // Clean up completed or invalid animations
      for (const edgeId of completedAnimations) {
        activeAnimationsRef.current.delete(edgeId)
      }
    }, [])
  )
}
